/*
 * Enums
 */
export enum InterchangeLevelEnum {
    standard,
    downgrade,
    lvl2,
    lvl3
}

// TODO (jacob): Double check and make sure these are not used bare to display
export enum FeeGroupEnum {
    PROCESSOR = 'processor',
    ADDITIONAL = 'additional',
    ASSESSMENT = 'assessment',
    NON_NEGOTIABLE = 'nonNegotiable',
    INTERCHANGE = 'interchange',
    UNCATEGORIZED = 'uncategorized'
}

// Fee grouping flags for that come from the pdf parsing process
export enum FeeGroupingFlags {
    CATEGORIZATION_NEEDED = 'This fee has not yet been added to our database.',
    MISCALCULATION = 'The statement appears to have made mathematical errors on this fee.',
    DEFAULT = 'This fee could be prevented in the future.',
    INEXACT_MATCH = 'This fee is very similar to one in our database, but we aren\'t fully confident.'
}

/*
 * Helper Classes
 */

/**
 * This is used in the fee-grouping service to map interchange fee
 * descriptions to their associated program. It's intended to be as
 * small as possible so that we can store an array of ~10,000 locally,
 * so that mapping can be quick.
 */
export class InterchangeDescriptor {
    description: string;
    programName: string;
    programId: string;
}

/**
 * This contains all the remaining data that's left out of the
 * InterchangeDescriptor
 */
export class InterchangeProgram {
    name: string;
    rate: number;
    fixed: number;
    level: InterchangeLevelEnum;
}

export class ProcessorFeeDetail {
    id: string;
    description: string;
    // TODO (jacob): I would like these to be private, but I can't 
    //               do that without breaking a lot of code and affecting
    //               serialization
    amount: number;
    volume: number;
    monthlyTransactions: number;
    transactionFee: number;
    transactionRate: number;
    transRatePercent: number;

    calculateAmount() {
        const derived = this.volume * this.transactionRate
            + this.monthlyTransactions * this.transactionFee;
        const epsilon = 0.005;
        if (Math.abs(derived) < epsilon && this.amount > epsilon) {
            return;
        }
        this.amount = derived;
    }

    setVolume(volume: number) {
        this.volume = volume;
        this.calculateAmount();
    }

    setTransactionCount(count: number) {
        this.monthlyTransactions = count;
        this.calculateAmount();
    }

    setTransactionFee(fee: number) {
        this.transactionFee = fee;
        this.calculateAmount();
    }

    setTransactionRate(rate: number) {
        if (rate > 1)
            throw new Error('rates cannot be greater than 1');

        this.transactionRate = rate;
        this.transRatePercent = rate * 100;
        this.calculateAmount();
    }

    setTransactionRatePercent(percent: number) {
        if (percent > 100)
            throw new Error('percentages cannot be greater than 1');

        this.transRatePercent = percent;
        this.transactionRate = percent / 100;
        this.calculateAmount();
    }

    constructor(fee?: Partial<ProcessorFeeDetail>) {
        this.id = fee?.id || crypto.randomUUID();
        this.description = fee?.description ?? '';
        this.amount = fee?.amount ?? 0;
        this.volume = fee?.volume ?? 0;
        this.monthlyTransactions = fee?.monthlyTransactions ?? 0;
        this.transactionFee = fee?.transactionFee ?? 0;

        // I don't know under what circumstances one or the other will be set,
        // so I'm covering my bases.
        if (fee?.transRatePercent == null && fee?.transactionRate == null) {
            this.transactionRate = 0;
            this.transRatePercent = 0;
        } else if (fee?.transRatePercent == null || fee.transRatePercent === 0) {
            this.transactionRate = fee.transactionRate;
            this.transRatePercent = this.transactionRate * 100;
        } else if (fee?.transactionRate == null || fee.transactionRate === 0) {
            this.transRatePercent = fee.transRatePercent;
            this.transactionRate = this.transRatePercent / 100;
        } else {
            this.transactionRate = fee.transactionRate;
            this.transRatePercent = fee.transRatePercent;
        }
    }
}

/*
 * Fees
 */

export class BaseFee {
    id: string;
    label: string;
    amount: number;
    isFlagged: boolean;
    flaggedDescription: string | undefined;
    bestGuessedCategory?: string;

    constructor(fee?: Partial<BaseFee>) {
        this.id = fee?.id || crypto.randomUUID();
        this.label = fee?.label ?? '';
        this.amount = fee?.amount ?? 0;
        this.isFlagged = fee?.isFlagged ?? false;
        this.flaggedDescription = fee?.flaggedDescription ?? '';
        this.bestGuessedCategory = fee?.bestGuessedCategory ?? '';
    }
}

// TODO (jacob): long term -- remove totalAmount and replace totalVolume with volume to be
//               consistent with BaseFee.
export class ProcessorFee extends BaseFee {
    totalVolume: number;
    totalAmount: number;
    totalTransactions: number;
    feeDetails: ProcessorFeeDetail[];

    constructor(fee?: Partial<ProcessorFee>) {
        super(fee);
        this.id = fee?.id || crypto.randomUUID();
        this.feeDetails = fee?.feeDetails != null
            ? fee.feeDetails.map(f => new ProcessorFeeDetail(f))
            : [new ProcessorFeeDetail({
                description: fee?.label,
                amount: fee?.amount,
                volume: fee?.totalVolume,
                monthlyTransactions: fee?.totalTransactions
            })];

        this.calculateTotal();
    }

    calculateTotal(recalculateAmounts = true) {
        this.amount = 0;
        this.totalAmount = 0;
        this.totalVolume = 0;
        this.totalTransactions = 0;
        for (const feeDetail of this.feeDetails) {
            if (recalculateAmounts) {
                feeDetail.calculateAmount();
            }
            // NOTE: amount and totalAmount should always be identical values.
            //       default to totalAmount if there is a discrepancy.
            this.amount += feeDetail.amount;
            this.totalAmount += feeDetail.amount;
            this.totalVolume += feeDetail.volume;
            this.totalTransactions += feeDetail.monthlyTransactions;
        }
    }
}

// TODO (jacob): Potentially remove?
export class NonNegotiableFee extends BaseFee {
    constructor(fee?: Partial<NonNegotiableFee>) {
        super(fee);
        this.id = fee?.id || crypto.randomUUID();
    }
}

export class InterchangeFee extends NonNegotiableFee {
    isPadded: boolean; //TODO (jacob): remove is padded and use trueAmount == null to toggle
    trueAmount: number | undefined;

    constructor(fee?: Partial<InterchangeFee>) {
        super(fee);
        this.id = fee?.id || crypto.randomUUID();
        this.isPadded = fee?.isPadded ?? false;
        this.trueAmount = fee?.trueAmount;
    }
}

/*
 * Fee Groupings
 */

export class FeeGroup {
    name: string;
    abbrev: string;
    displayName: string;
}

export class BaseFeeGrouping {
    totalAmount: number;
    fees: BaseFee[] = [];
    friendlyName?: string;

    constructor(input: Partial<BaseFeeGrouping> = {}) {
        this.fees = input.fees ?? [];
        this.friendlyName = input.friendlyName;
        this.recalculateTotal();
    }

    popAt(i: number): BaseFee {
        const fee = this.fees.splice(i, 1)[0];
        this.recalculateTotal();

        return fee;
    }

    push(fee: BaseFee = new BaseFee()) {
        this.fees.push(fee);
        this.recalculateTotal();
    }

    clear() {
        this.fees = [];
        this.recalculateTotal();
    }

    length() {
        return this.fees.length;
    }

    recalculateTotal(recalculateAmounts = true) {
        this.totalAmount = 0;
        for (const fee of this.fees)
            this.totalAmount += fee.amount;
    }

    removeFeeById(id: string): BaseFee | undefined {
        const index = this.fees.findIndex(fee => fee.id === id);
        if (index === -1) {
            return undefined;
        }
        return this.popAt(index);
    }
}

export class ProcessorFeeGrouping extends BaseFeeGrouping {
    totalVolume: number;
    totalTransactions: number;
    fees: ProcessorFee[];
    displayName?: string;

    constructor(input: Partial<ProcessorFeeGrouping> = {}) {
        super(input);
    }

    push(fee?: BaseFee) {
        this.fees.push(new ProcessorFee(fee));
        this.recalculateTotal();
    }

    recalculateTotal(recalculateAmounts = true) {
        this.totalAmount = 0;
        this.totalVolume = 0;
        this.totalTransactions = 0;

        for (const fee of this.fees) {
            fee.calculateTotal(recalculateAmounts);
            this.totalAmount += fee.totalAmount;
            this.totalVolume += fee.totalVolume;
            this.totalTransactions += fee.totalTransactions;
        }
    }
}

export class NonNegotiableFeeGrouping extends BaseFeeGrouping {
    fees: NonNegotiableFee[];
    constructor(input: Partial<NonNegotiableFeeGrouping> = {}) {
        super(input);
    }

    push(fee?: BaseFee) {
        this.fees.push(new NonNegotiableFee(fee));
        this.recalculateTotal();
    }

}

export class InterchangeFeeGrouping extends BaseFeeGrouping {
    fees: InterchangeFee[];
    constructor(input: Partial<InterchangeFeeGrouping> = {}) {
        super(input);
    }

    push(fee?: BaseFee) {
        this.fees.push(new InterchangeFee(fee));
        this.recalculateTotal();
    }

}

// abbrev is only used in classify-pdf-selection-dialog
// TODO (jacob): Refactor
export const FeeGroupingOptions = [
    {
        name: FeeGroupEnum.ADDITIONAL,
        abbrev: 'Addtnl',
        displayName: 'Additional Fees'
    },
    {
        name: FeeGroupEnum.ASSESSMENT,
        abbrev: 'Assess',
        displayName: 'Assessment Fees'
    },
    {
        name: FeeGroupEnum.NON_NEGOTIABLE,
        abbrev: 'NonNeg',
        displayName: 'Other Non-Negotiable Fees'
    },
    {
        name: FeeGroupEnum.INTERCHANGE,
        abbrev: 'Intchg',
        displayName: 'Interchange Fees'
    },
    {
        name: FeeGroupEnum.PROCESSOR,
        abbrev: 'Proc',
        displayName: 'Processor Fees'
    }
];


export class FeeGroupings {
    processor: ProcessorFeeGrouping;
    additional: BaseFeeGrouping;
    assessment: BaseFeeGrouping;
    nonNegotiable: NonNegotiableFeeGrouping;
    interchange: InterchangeFeeGrouping;
    uncategorized?: BaseFeeGrouping;

    constructor(input: Partial<FeeGroupings> = {}) {
        this.processor = new ProcessorFeeGrouping({
            fees: input.processor?.fees?.map(f => new ProcessorFee(f)),
            friendlyName: input.processor?.friendlyName
        });
        this.additional = new BaseFeeGrouping({
            fees: input.additional?.fees?.map(f => new BaseFee(f)),
            friendlyName: input.additional?.friendlyName
        });
        this.assessment = new BaseFeeGrouping({
            fees: input.assessment?.fees?.map(f => new BaseFee(f)),
            friendlyName: input.assessment?.friendlyName
        });
        this.nonNegotiable = new NonNegotiableFeeGrouping({
            fees: input.nonNegotiable?.fees?.map(f => new NonNegotiableFee(f)),
            friendlyName: input.nonNegotiable?.friendlyName
        });
        this.interchange = new InterchangeFeeGrouping({
            fees: input.interchange?.fees?.map(f => new InterchangeFee(f)),
            friendlyName: input.interchange?.friendlyName
        });
        this.uncategorized = new BaseFeeGrouping({
            fees: input.uncategorized?.fees?.map(f => new BaseFee(f)),
            friendlyName: input.uncategorized?.friendlyName
        });
    }

}
